// THE planner: days from today (infinite list), meals per slot, shopping trips,
// "add planned groceries", shopping schedule, past days, mealplans.
import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useFormatDay, useDiet, useHeader, useToday } from '../hooks';
import { usePlanner } from '../store/planner';
import { useShopping } from '../store/shopping';
import { useLibrary, useRecipeLookup } from '../store/library';
import { addDays, weekdayOf, WEEKDAYS } from '../logic/dates';
import { HOLIDAYS, holidaysByDay, type Occurrence } from '../logic/holidays';
import { useSettings } from '../store/settings';
import { ingredientsAlreadyInPantry, plannedRecipeIds } from '../logic/grocery';
import { applyMealplan } from '../logic/schedule';
import Icon from '../components/Icon';
import Photo from '../components/Photo';
import Sheet from '../components/Sheet';
import NutritionSheet from '../components/NutritionSheet';
import { RecipePickerSheet, SLOTS } from '../components/Pickers';
import SelectionBar, { useSelection } from '../components/SelectionBar';
import { notify, showMenu } from '../components/Dialogs';
import { Button, Caption, Chip, IconButton, ListItem, Row, SectionHeader, Stepper } from '../components/ui';
import { perServingLines } from './MealplanScreen';
import { Fade } from '../components/Motion';
import { font, radius, spacing, usePalette } from '../theme';
import type { Day, PlanEntry, Recipe, ShoppingSchedule, Slot, Weekday } from '../types';

function DayRow({
  day, today, entries, trip, selecting, isSelected, onToggle, onMenu, onOpen, holidays,
}: {
  day: Day; today: Day; entries: PlanEntry[]; trip: boolean; selecting: boolean; isSelected: (id: string) => boolean; onToggle: (id: string) => void;
  onMenu: () => void; onOpen: (recipeId: string) => void; holidays: Occurrence[];
}) {
  const { t } = useTranslation();
  const p = usePalette();
  const lookup = useRecipeLookup();
  const diet = useDiet();
  const formatDay = useFormatDay();
  const isToday = day === today;
  const past = day < today;
  return (
    <View style={{ gap: spacing.s, padding: spacing.m, borderRadius: radius.l, backgroundColor: isToday ? `${p.success}22` : 'transparent', opacity: past ? 0.55 : 1 }}>
      <Row style={{ flexWrap: 'nowrap' }}>
        <Text style={{ flex: 1, fontSize: font.headline, fontWeight: '700', color: isToday ? p.success : past ? p.secondaryText : p.text }}>
          {t(`weekdays.${weekdayOf(day)}`)}
        </Text>
        {trip ? <Icon name="grocery" size={18} color={p.success} /> : null}
        <Caption style={{ fontVariant: ['tabular-nums'] }}>{formatDay(day, { day: 'numeric', month: 'short' })}</Caption>
        {!past ? <Fade visible={!selecting}><IconButton icon="more" label={t('common.more')} onPress={onMenu} /></Fade> : null}
      </Row>
      {holidays.length ? (
        <Row>
          {holidays.map(h => (
            <Text key={h.id} style={{ fontSize: font.footnote, fontWeight: '600', color: p.text, backgroundColor: p.card, borderRadius: radius.pill, paddingHorizontal: spacing.m, paddingVertical: 4, overflow: 'hidden' }}>
              {`${HOLIDAYS[h.id].decor[0]} ${t(`holidays.${h.id}.name`)}${day < h.main ? ` · ${t('holiday.eve')}` : ''}`}
            </Text>
          ))}
        </Row>
      ) : null}
      {SLOTS.map(s => {
        const inSlot = entries.filter(e => e.slot === s.slot);
        if (!inSlot.length) return null;
        return (
          <View key={s.slot} style={{ gap: spacing.xs }}>
            <SectionHeader title={t(`planner.${s.slot}`)} />
            {inSlot.map(e => {
              const r = lookup(e.recipeId);
              return (
                <ListItem
                  key={e.id}
                  title={r?.title ?? '—'}
                  diets={diet.issues(r)}
                  subtitle={r ? `${t('common.servings', { count: r.servings })} · ${t('common.minutes', { count: r.durationMin })}` : undefined}
                  leading={<Photo media={r?.image} seed={e.recipeId} style={{ width: 40, height: 40 }} iconSize={18} />}
                  selected={selecting && !past ? isSelected(e.id) : undefined}
                  onPress={selecting ? (past ? undefined : () => onToggle(e.id)) : () => onOpen(e.recipeId)}
                />
              );
            })}
          </View>
        );
      })}
    </View>
  );
}

function ScheduleSheet({ visible, onClose, today }: { visible: boolean; onClose: () => void; today: Day }) {
  const { t } = useTranslation();
  const { schedule, setSchedule } = usePlanner();
  const [draft, setDraft] = useState<ShoppingSchedule>(schedule ?? { kind: 'everyNDays', n: 7 });
  React.useEffect(() => {
    if (visible) setDraft(schedule ?? { kind: 'everyNDays', n: 7 });
  }, [visible, schedule]);
  return (
    <Sheet visible={visible} onClose={onClose} title={t('schedule.title')}>
      <Row>
        <Chip label={t('schedule.everyDays')} selected={draft.kind === 'everyNDays'} onPress={() => setDraft({ kind: 'everyNDays', n: 7 })} />
        <Chip label={t('schedule.weekday')} selected={draft.kind === 'weekday'} onPress={() => setDraft({ kind: 'weekday', weekday: 'sunday', everyNWeeks: 1 })} />
      </Row>
      {draft.kind === 'everyNDays' ? (
        <>
          <Caption>{t('schedule.everyN', { count: draft.n })}</Caption>
          <Stepper label={t('schedule.everyDays')} value={draft.n} min={1} max={365} onChange={n => setDraft({ kind: 'everyNDays', n })} />
        </>
      ) : (
        <>
          <Caption>{t('schedule.weekdayLabel')}</Caption>
          <Row>
            {WEEKDAYS.map(w => (
              <Chip key={w} label={t(`weekdays.${w}`)} selected={draft.weekday === w} onPress={() => setDraft({ ...draft, weekday: w as Weekday })} />
            ))}
          </Row>
          <Caption>{t('schedule.everyWeeks', { count: draft.everyNWeeks })}</Caption>
          <Stepper label={t('schedule.weekday')} value={draft.everyNWeeks} min={1} max={52} onChange={n => setDraft({ ...draft, everyNWeeks: n })} />
        </>
      )}
      <Button label={t('common.save')} icon="check" onPress={() => { setSchedule(draft, today); onClose(); }} iconOnly />
      {schedule ? <Button label={t('schedule.turnOff')} variant="danger" onPress={() => { setSchedule(null, today); onClose(); }} /> : null}
    </Sheet>
  );
}

export default function PlannerScreen() {
  const { t } = useTranslation();
  const nav = useNav();
  const today = useToday();
  const formatDay = useFormatDay();
  const { entries, tripDates, plan, toggleTrip, removeEntries, housekeep, addEntries } = usePlanner();
  const addToGrocery = useShopping(s => s.addRecipes);
  const pantry = useShopping(s => s.pantry);
  const mealplans = useLibrary(s => s.mealplans);
  const lookup = useRecipeLookup();
  const [count, setCount] = useState(56);
  const [pick, setPick] = useState<{ day: Day; slot: Slot } | null>(null);
  const [sheet, setSheet] = useState<'past' | 'schedule' | 'nutrition' | 'mealplan' | null>(null);
  const [mealplanStart, setMealplanStart] = useState<Day>(today);
  const sel = useSelection();

  useFocusEffect(useCallback(() => housekeep(today), [today, housekeep]));

  const byDay = useMemo(() => {
    const map = new Map<Day, PlanEntry[]>();
    for (const e of entries) map.set(e.date, [...(map.get(e.date) ?? []), e]);
    return map;
  }, [entries]);
  const trips = useMemo(() => new Set(tripDates), [tripDates]);
  const days = useMemo(() => Array.from({ length: count }, (_, i) => addDays(today, i)), [today, count]);

  const addPlannedGroceries = () => {
    const recipes = plannedRecipeIds(entries, today).map(lookup).filter((r): r is Recipe => !!r);
    if (!recipes.length) return notify(t('planner.nothingPlanned'));
    const finish = (exclude: Set<string>) => {
      addToGrocery(recipes, exclude);
      nav.navigate('Grocery');
    };
    const inPantry = ingredientsAlreadyInPantry(recipes, pantry);
    if (!inPantry.size) return finish(new Set());
    showMenu(
      [
        { label: t('planner.deduplicate'), icon: 'checklist', onPress: () => finish(inPantry) },
        { label: t('planner.addDouble'), icon: 'plus', onPress: () => finish(new Set()) },
      ],
      t('planner.pantryDialogTitle'),
    );
  };

  useHeader(
    {
      title: '',
      headerRight: () =>
        sel.editing ? (
          <IconButton icon="check" label={t('common.done')} onPress={sel.stop} />
        ) : (
          <Row gap={spacing.m}>
            <IconButton icon="cartAdd" label={t('planner.addGroceries')} onPress={addPlannedGroceries} />
            <IconButton
              icon="more"
              label={t('common.more')}
              onPress={() =>
                showMenu([
                  { label: t('planner.showPast'), icon: 'clock', onPress: () => setSheet('past') },
                  { label: t('planner.schedule'), icon: 'grocery', onPress: () => setSheet('schedule') },
                  { label: t('planner.applyMealplan'), icon: 'mealplan', onPress: () => setSheet('mealplan') },
                  { label: t('recipe.nutrition'), icon: 'nutrition', onPress: () => setSheet('nutrition') },
                  { label: t('common.select'), icon: 'checklist', onPress: sel.start },
                ])
              }
            />
          </Row>
        ),
    },
    [sel.editing, entries, pantry, t],
  );

  const dayMenu = (day: Day) =>
    showMenu(
      [
        ...SLOTS.map(s => ({ label: t(`planner.${s.slot}`), icon: s.icon, onPress: () => setPick({ day, slot: s.slot }) })),
        trips.has(day)
          ? { label: t('planner.removeTrip'), icon: 'close' as const, onPress: () => toggleTrip(day) }
          : { label: t('planner.planTrip'), icon: 'cartAdd' as const, onPress: () => toggleTrip(day) },
      ],
      formatDay(day, { weekday: 'long', day: 'numeric', month: 'long' }),
    );

  const traditions = useSettings(s => s.traditions);
  const holidayDays = useMemo(() => holidaysByDay(addDays(today, -60), addDays(today, count), traditions), [today, count, traditions]);

  const renderDay = (day: Day) => (
    <DayRow
      day={day}
      today={today}
      entries={byDay.get(day) ?? []}
      trip={trips.has(day)}
      selecting={sel.editing}
      isSelected={sel.has}
      onToggle={sel.toggle}
      onMenu={() => dayMenu(day)}
      onOpen={id => nav.navigate('Recipe', { id })}
      holidays={holidayDays.get(day) ?? []}
    />
  );

  // Nutrition: the next 7 days, per serving of each planned meal.
  const weekLines = perServingLines(entries.filter(e => e.date >= today && e.date < addDays(today, 7)).map(e => lookup(e.recipeId)).filter((r): r is Recipe => !!r));

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        data={days}
        keyExtractor={d => d}
        renderItem={({ item }) => renderDay(item)}
        onEndReached={() => setCount(c => c + 14)}
        onEndReachedThreshold={0.5}
        contentContainerStyle={{ padding: spacing.s, paddingBottom: 120 }}
      />
      {sel.editing ? <SelectionBar count={sel.ids.size} onDelete={() => { removeEntries([...sel.ids]); sel.stop(); }} /> : null}

      <RecipePickerSheet
        visible={!!pick}
        onClose={() => setPick(null)}
        onPick={r => {
          if (pick) plan(pick.day, pick.slot, [r.id]);
          setPick(null);
        }}
      />
      <Sheet visible={sheet === 'past'} onClose={() => setSheet(null)} title={t('planner.pastDays')} full>
        {Array.from({ length: 31 }, (_, i) => addDays(today, -31 + i)).map(day => (
          <View key={day}>{renderDay(day)}</View>
        ))}
      </Sheet>
      <ScheduleSheet visible={sheet === 'schedule'} onClose={() => setSheet(null)} today={today} />
      <NutritionSheet visible={sheet === 'nutrition'} onClose={() => setSheet(null)} lines={weekLines} servings={7} title={t('planner.title')} caption={t('nutrition.perDayWeek')} />
      <Sheet visible={sheet === 'mealplan'} onClose={() => setSheet(null)} title={t('planner.applyMealplan')}>
        <Caption>{t('planner.applyFrom')}</Caption>
        <Row>
          {Array.from({ length: 14 }, (_, i) => addDays(today, i)).map(d => (
            <Chip key={d} label={d === today ? t('common.today') : formatDay(d)} selected={d === mealplanStart} onPress={() => setMealplanStart(d)} />
          ))}
        </Row>
        {Object.values(mealplans).length ? (
          Object.values(mealplans).map(m => (
            <ListItem
              key={m.id}
              title={m.title}
              subtitle={t('mealplan.days', { count: m.cycleDays })}
              icon="mealplan"
              onPress={() => {
                addEntries(applyMealplan(m, mealplanStart));
                setSheet(null);
                notify(t('planner.applied'));
              }}
            />
          ))
        ) : (
          <Caption>{t('common.empty')}</Caption>
        )}
      </Sheet>
    </View>
  );
}
